import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAppConfig } from '@/config/appConfig.js';
import { getFirebaseStatus } from '@/infrastructure/firebase/firebaseStatus.js';
import { firebaseFunctions } from '@/infrastructure/firebase/firebaseFunctions.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useIntegrationStore } from '@/stores/useIntegrationStore.js';
import { SelfTestService, type DiagnosticResult } from '@/domain/diagnostics/SelfTestService.js';
import { APP_VERSION, DISCLAMER_TEXT, type IntegrationStats } from '@k-ucap/shared';
import { getMessageDefinitions } from '@k-ucap/message-dictionary';
import { ModeBadge } from '@/components/common/ModeBadge.js';
import { ErrorCenter } from '@/components/error-center/ErrorCenter.js';

import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';

type CheckStatus = 'idle' | 'loading' | 'connected' | 'failed' | 'not_configured';

interface ConnectionStatus {
  auth: CheckStatus;
  firestore: CheckStatus;
  functions: CheckStatus;
}

export const SettingsPage = () => {
  const config = getAppConfig();
  const status = getFirebaseStatus();
  const dictVersion = getMessageDefinitions().length;
  const userProfile = useAuthStore((s) => s.userProfile);
  const globalRole = useAuthStore((s) => s.globalRole);
  const permissions = useAuthStore((s) => s.permissions);
  const projectMemberships = useAuthStore((s) => s.projectMemberships);
  const authUser = useAuthStore((s) => s.authUser);
  const accessibleProjects = useAuthStore((s) => s.accessibleProjects);
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const authStatus = useAuthStore((s) => s.authStatus);
  const isLoading = useAuthStore((s) => s.isLoading);
  const repositories = useRepositories();

  const virtualAdapter = getGlobalVirtualMessageAdapter();
  const eventBusStatus = virtualAdapter.getStatus();
  const eventBusHistoryCount = virtualAdapter.getHistory().length;

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    auth: 'idle',
    firestore: 'idle',
    functions: 'idle',
  });
  const [checking, setChecking] = useState(false);
  const [selfTestResults, setSelfTestResults] = useState<DiagnosticResult[] | null>(null);
  const [selfTestRunning, setSelfTestRunning] = useState(false);
  const adapters = useIntegrationStore((s) => s.adapters);
  const adapterCount = useMemo(() => Object.values(adapters).length, [adapters]);
  const totalStats = useMemo(() => {
    const empty: IntegrationStats = { rxCount: 0, txCount: 0, rxBytes: 0, txBytes: 0, errors: 0, reconnects: 0 };
    return Object.values(adapters).reduce(
      (total, adapter) => ({
        rxCount: total.rxCount + adapter.stats.rxCount,
        txCount: total.txCount + adapter.stats.txCount,
        rxBytes: total.rxBytes + adapter.stats.rxBytes,
        txBytes: total.txBytes + adapter.stats.txBytes,
        errors: total.errors + adapter.stats.errors,
        reconnects: total.reconnects + adapter.stats.reconnects,
      }),
      empty
    );
  }, [adapters]);

  const errorCount = totalStats.errors;
  const rxCount = totalStats.rxCount;
  const txCount = totalStats.txCount;

  const handleCheckConnection = useCallback(async () => {
    setChecking(true);
    setConnectionStatus({ auth: 'loading', firestore: 'loading', functions: 'loading' });

    const next: ConnectionStatus = { auth: 'not_configured', firestore: 'not_configured', functions: 'not_configured' };

    if (status.mode !== 'FIREBASE' || !firebaseFunctions) {
      setConnectionStatus(next);
      setChecking(false);
      return;
    }

    // Auth check
    next.auth = authUser ? 'connected' : 'failed';

    // Firestore check: read projects
    try {
      await repositories.projectRepository.findAll();
      next.firestore = 'connected';
    } catch {
      next.firestore = 'failed';
    }

    // Functions check: healthCheck (HTTP) + getSystemInfo (onCall)
    try {
      const healthUrl = `https://${config.functionsRegion}-${config.firebase.projectId}.cloudfunctions.net/healthCheck`;
      const res = await fetch(healthUrl);
      if (res.ok) {
        next.functions = 'connected';
      } else {
        next.functions = 'failed';
      }
    } catch {
      next.functions = 'failed';
    }

    // getSystemInfo onCall sanity check (only if auth present)
    if (authUser && next.functions === 'connected') {
      try {
        const functions = getFunctions(undefined, config.functionsRegion);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getSystemInfo = httpsCallable(functions, 'getSystemInfo' as any);
        await getSystemInfo({});
      } catch {
        // onCall 실패해도 healthCheck가 통과하면 functions는 기본적으로 연결된 것으로 간주
      }
    }

    setConnectionStatus(next);
    setChecking(false);
  }, [authUser, config.firebase.projectId, config.functionsRegion, repositories.projectRepository, status.mode]);

  const items = [
    { label: 'App Name', value: config.appName },
    { label: 'App Version', value: APP_VERSION },
    { label: 'Firebase Mode', value: status.mode },
    { label: 'Firebase Project 연결', value: status.initialized ? '연결' : '미연결' },
    { label: 'Project ID', value: config.firebase.projectId || '미설정' },
    { label: 'Functions Region', value: config.functionsRegion },
    { label: 'Environment', value: config.appEnv },
    { label: 'Data Environment', value: config.dataEnvironment },
    { label: 'Message Dictionary Version', value: `${dictVersion}종` },
    { label: 'Cesium 상태', value: config.cesiumIonToken ? 'Token 설정됨' : 'Token 미설정' },
    { label: 'Repository Mode', value: status.mode === 'FIREBASE' ? 'Firestore' : 'InMemory' },
  ];

  const userItems = userProfile
    ? [
        { label: 'UID', value: userProfile.uid },
        { label: 'Email', value: userProfile.email },
        { label: 'Display Name', value: userProfile.displayName },
        { label: 'Global Role', value: globalRole },
        { label: 'Status', value: userProfile.status },
        { label: 'Auth Provider', value: userProfile.authProvider },
      ]
    : [];

  const renderStatusChip = (s: CheckStatus) => {
    if (s === 'loading') return <CircularProgress size={16} />;
    if (s === 'connected') return <Chip label="CONNECTED" size="small" color="success" />;
    if (s === 'failed') return <Chip label="FAILED" size="small" color="error" />;
    if (s === 'not_configured') return <Chip label="NOT CONFIGURED" size="small" color="warning" />;
    return <Chip label="-" size="small" />;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        설정
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                앱 정보
              </Typography>
              <List dense>
                {items.map((item) => (
                  <ListItem key={item.label} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                    {item.label === 'Firebase Mode' ? (
                      <ModeBadge mode={status.mode} />
                    ) : (
                      <Typography variant="body1">{item.value}</Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Diagnostics
              </Typography>
              <List dense>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Auth Status
                  </Typography>
                  <Typography variant="body1">{authStatus}</Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading
                  </Typography>
                  <Typography variant="body1">{isLoading ? 'YES' : 'NO'}</Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Global Role
                  </Typography>
                  <Typography variant="body1">{globalRole}</Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected Project
                  </Typography>
                  <Typography variant="body1">
                    {selectedProject ? `${selectedProject.name} (${selectedProject.id})` : '미선택'}
                  </Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Demo Mode
                  </Typography>
                  <Typography variant="body1">{selectedProject?.isDemo ? 'YES' : 'NO'}</Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Accessible Projects
                  </Typography>
                  <Typography variant="body1">{accessibleProjects.length}개</Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Virtual Event Bus
                  </Typography>
                  <Typography variant="body1">
                    {eventBusStatus} ({eventBusHistoryCount} events)
                  </Typography>
                </ListItem>
              </List>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Firebase 연결 확인
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ width: 80 }}>
                    Auth
                  </Typography>
                  {renderStatusChip(connectionStatus.auth)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ width: 80 }}>
                    Firestore
                  </Typography>
                  {renderStatusChip(connectionStatus.firestore)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ width: 80 }}>
                    Functions
                  </Typography>
                  {renderStatusChip(connectionStatus.functions)}
                </Box>
              </Box>
              <Button variant="outlined" size="small" onClick={handleCheckConnection} disabled={checking}>
                연결 확인
              </Button>
              {status.mode !== 'FIREBASE' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  현재 Mock Mode입니다. Firebase 연결 확인은 Firebase Mode에서만 동작합니다.
                </Alert>
              )}
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                통합 연동 상태
              </Typography>
              <List dense>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    등록된 Adapter
                  </Typography>
                  <Typography variant="body1">{adapterCount}개</Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    총 RX / TX
                  </Typography>
                  <Typography variant="body1">
                    {rxCount} / {txCount}
                  </Typography>
                </ListItem>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Errors
                  </Typography>
                  <Typography variant="body1">{errorCount}</Typography>
                </ListItem>
              </List>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                전체 시스템 점검
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  setSelfTestRunning(true);
                  setSelfTestResults(null);
                  try {
                    const summary = await SelfTestService.run(selectedProject?.id);
                    setSelfTestResults(summary.results);
                  } catch (err) {
                    setSelfTestResults([
                      {
                        component: 'Self Test',
                        status: 'fail',
                        durationMs: 0,
                        error: err instanceof Error ? err.message : String(err),
                        recommendation: '잠시 후 다시 시도하세요.',
                      },
                    ]);
                  } finally {
                    setSelfTestRunning(false);
                  }
                }}
                disabled={selfTestRunning}
                sx={{ mb: 2 }}
              >
                {selfTestRunning ? '점검 중...' : '전체 시스템 점검'}
              </Button>
              {selfTestResults && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Component</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selfTestResults.map((result) => (
                      <TableRow key={result.component}>
                        <TableCell>{result.component}</TableCell>
                        <TableCell>
                          <Chip
                            label={result.status}
                            size="small"
                            color={
                              result.status === 'pass' ? 'success' : result.status === 'warn' ? 'warning' : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>{result.durationMs}ms</TableCell>
                        <TableCell>{result.error ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <ErrorCenter />
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                현재 사용자
              </Typography>
              {userProfile ? (
                <List dense>
                  {userItems.map((item) => (
                    <ListItem key={item.label} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="body1">
                        {item.value}
                        {item.label === 'UID' && (
                          <Button
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={() => navigator.clipboard.writeText(String(item.value))}
                          >
                            복사
                          </Button>
                        )}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2">로그인 정보가 없습니다.</Typography>
              )}
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Project Membership
              </Typography>
              {projectMemberships.length > 0 ? (
                <List dense>
                  {projectMemberships.map((m) => (
                    <ListItem key={m.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {m.projectId}
                      </Typography>
                      <Typography variant="body1">{m.projectRole}</Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2">가입된 프로젝트가 없습니다.</Typography>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {permissions.map((p) => (
                  <Chip key={p} label={p} size="small" />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            제품 정보
          </Typography>
          <Typography variant="body2">{DISCLAMER_TEXT}</Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
